import {useEffect, useRef} from 'react';
import {useSetAtom} from 'jotai';
import {isOnlineAtom, bootstrapAuthAtom} from './authAtoms';
import {probeReachability} from '@/lib/onlineState';
import {useDevice} from '@/hooks/useDevice.ts';

// ADDED 2026-08-31 (Duane): "the sync icon is turning even though I'm
// offline... offline is still not working correctly." Matches
// SYNC_POLL_INTERVAL_MS's cadence in useOfflineSync.ts (Duane picked
// "continuous lightweight probe" when asked, over a purely reactive
// check) — a real phone can sit foregrounded the whole time a field
// user is filling in a report with no focus/visibility change and no
// 'offline' event ever firing (or firing late — see
// lib/onlineState.ts's own top comment), so a fixed poll is the only
// thing that reliably notices a manually-disabled radio within a
// bounded time regardless of what else does or doesn't happen.
//
// SCOPED TO TOUCH/MOBILE DEVICES ONLY as of 2026-09-02 (Duane: "is that
// needed on desktop... I would assume desktop connection would be
// fairly stable"). The unreliable-`navigator.onLine` problem this poll
// exists for is specifically a mobile-radio problem (see this file's
// and lib/onlineState.ts's own comments) — desktop OS network stacks
// fire the native 'online'/'offline' events promptly and accurately,
// which this hook already reacts to instantly below, plus the
// focus/visibility recheck. Desktop (Admin/SuperAdmin) sessions also
// never queue offline mutations at all (see authAtoms.ts's
// `isFieldRole` gate on `enableOfflineCachePersistence`), so there's
// nothing there for a missed reconnect to even recover. See
// `isPhoneOrTablet` below — only that case gets the recurring
// `setInterval`. UPDATED 2026-09-12 (see the `handleOnline`/
// `recheckOnlineStatus`/initial-state comments further down in this
// file): desktop no longer gets the one-off probe on mount or the
// event-driven rechecks either — it seeds isOnline from
// navigator.onLine directly and leaves confirmation to the native
// 'online'/'offline' events and the app's own real GraphQL traffic.
const PROBE_INTERVAL_MS = 10000;

/**
 * Wires window online/offline events into Jotai state, then
 * bootstraps auth (see bootstrapAuthAtom for the offline-cache
 * fallback logic). Mount this once near the root of the app.
 */
export function useAuthBootstrap() {
   const setIsOnline = useSetAtom(isOnlineAtom);
   const bootstrapAuth = useSetAtom(bootstrapAuthAtom);
   // See PROBE_INTERVAL_MS's own comment above — only touch/mobile
   // devices get the recurring poll.
   const {isPhoneOrTablet} = useDevice();
   // Avoids piling up overlapping probes if one is slow (e.g. a
   // flaky connection eating most of its own timeout) right as
   // another trigger (poll tick, focus, 'online' event) fires — same
   // "ref guard" shape as useOfflineSync.ts's syncingRef.
   const probingRef = useRef(false);

   useEffect(() => {
      const runProbe = async () => {
         if (probingRef.current) return;
         probingRef.current = true;
         try {
            setIsOnline(await probeReachability());
         } finally {
            probingRef.current = false;
         }
      };

      // 'offline' is trusted immediately, no probe: the OS/browser
      // reporting an interface as fully down is itself a real signal —
      // there's nothing to gain by spending a request confirming it,
      // and every genuinely-offline path (useOfflineMutation's queue
      // check, apolloClient's offline-ceiling timeout) should react
      // the instant this fires, not PROBE's-worth of ms later.
      const handleOffline = () => setIsOnline(false);

      // UPDATED 2026-09-12 (Duane: "I'm seeing the offline probe call to
      // get logo-small on the desktop which isn't needed. For desktop,
      // rely on the normal calls and graphql to detect offline"). Every
      // trigger below used to call the same active probe
      // (lib/onlineState.ts's probeReachability, a HEAD request to
      // /images/logo-small.png) unconditionally on every device. The
      // 2026-09-02 change (see PROBE_INTERVAL_MS's own comment) already
      // kept the *recurring* setInterval poll off desktop on the theory
      // that a desktop OS's network stack reports 'online'/'offline'
      // promptly and accurately — but every event-driven trigger here
      // ('online' below, the focus/visibility recheck, and the very
      // first check on mount) still ran the probe anyway, which is
      // exactly the extra logo-small request Duane is seeing on desktop.
      // Desktop doesn't need it for two reasons already established in
      // this file's own history: that same prompt/accurate native event,
      // and — added 2026-08-31 per Duane's own "can't you detect network
      // issues from the graphQL network errors returned? If you didn't
      // detect offline before the API call you will know after" —
      // apolloClient.ts's authFailureLink already flips isOnlineAtom
      // from the outcome of every real query/mutation the app makes
      // (markOnlineFromRequestOutcome), so a desktop session that's
      // actually offline gets caught the moment its next normal GraphQL
      // call fails, with no need for a synthetic probe to find that out
      // sooner. Touch/mobile keeps the full active-probe behavior on
      // every trigger, unchanged — see lib/onlineState.ts's top comment
      // for why a phone's radio needs that extra confirmation and a
      // desktop connection doesn't.
      const handleOnline = () => {
         if (isPhoneOrTablet) {
            void runProbe();
         } else {
            setIsOnline(true);
         }
      };
      window.addEventListener('online', handleOnline);
      window.addEventListener('offline', handleOffline);

      // Desktop: no-op — see the UPDATED 2026-09-12 comment above. This
      // recheck exists for a phone that's been foregrounded the whole
      // time with no 'online' event ever firing (see PROBE_INTERVAL_MS's
      // own comment); a desktop reconnect is already caught by the
      // 'online' listener above and by ordinary GraphQL traffic.
      const recheckOnlineStatus = () => {
         if (!isPhoneOrTablet) return;
         if (document.visibilityState === 'visible') void runProbe();
      };
      document.addEventListener('visibilitychange', recheckOnlineStatus);
      window.addEventListener('focus', recheckOnlineStatus);

      // Touch/mobile: confirm the true starting state immediately with a
      // real probe (rather than waiting up to PROBE_INTERVAL_MS for the
      // first poll tick), then keep re-verifying on a fixed cadence for
      // as long as the app is open. Desktop: seed isOnline straight from
      // navigator.onLine — an obvious, request-free starting value — and
      // leave it to the 'online'/'offline' listeners and real GraphQL
      // traffic from here on, per the UPDATED 2026-09-12 comment above.
      if (isPhoneOrTablet) {
         void runProbe();
      } else {
         setIsOnline(typeof navigator !== 'undefined' ? navigator.onLine : true);
      }
      const interval = isPhoneOrTablet ? setInterval(() => void runProbe(), PROBE_INTERVAL_MS) : null;

      return () => {
         window.removeEventListener('online', handleOnline);
         window.removeEventListener('offline', handleOffline);
         document.removeEventListener('visibilitychange', recheckOnlineStatus);
         window.removeEventListener('focus', recheckOnlineStatus);
         if (interval) clearInterval(interval);
      };
   }, [setIsOnline, isPhoneOrTablet]);

   useEffect(() => {
      void bootstrapAuth();
   }, [bootstrapAuth]);
}
